import type {AsyncStackData} from '@github-ui/pull-requests/page-data/payloads/header'
import {useQuery} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useMemo} from 'react'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'

function stackDataQueryKey(repoNameWithOwner: string, pullRequestNumber: number) {
  return [PageData.stack, repoNameWithOwner, pullRequestNumber] as const
}

const StackDataSchema = Type.Object({
  id: Type.Number(),
  stack: {
    baseBranch: Type.String(),
    id: Type.Number(),
    repositoryOwnerLogin: Type.String(),
    repositoryName: Type.String(),
    position: Type.Number(),
    size: Type.Number(),
    pulls: Type.Array(
      Type.Object({
        headBranch: Type.String(),
        isUnstackable: Type.Boolean(),
        title: Type.String(),
        number: Type.Number(),
        state: Type.String(),
        url: Type.String(),
      }),
    ),
  },
})

const StackDataValidator = Compile(StackDataSchema)

export function useStackData(repoNameWithOwner: string, pullRequestNumber: number) {
  const {sendAnalyticsEvent} = useAnalytics()

  const persister = useMemo(
    () =>
      createPersister({
        validator: StackDataValidator,
        sendAnalyticsEvent,
      }),
    [sendAnalyticsEvent],
  )

  return useQuery<AsyncStackData>({
    queryKey: stackDataQueryKey(repoNameWithOwner, pullRequestNumber),
    queryFn: async () => {
      const response = await verifiedFetchJSON(
        `/${repoNameWithOwner}/pull/${pullRequestNumber}/page_data/${PageData.stack}`,
      )
      if (!response.ok) {
        throw new Error(`Failed to fetch stack data: HTTP ${response.status}`)
      }
      return response.json()
    },
    staleTime: Infinity,
    persister,
  })
}
